import BaseService from "scripts/services/baseService";

// Cap the number of curations retained so an admin browsing many large
// curations in one session doesn't grow memory unbounded. Least-recently-used
// entries are evicted first.
const MAX_ENTRIES = 5;

/**
 * In-memory, per-session cache of curation "all-media" responses, keyed by
 * curation id. Used to render the Manage Curation grid instantly on revisit
 * (the collection revalidates in the background). The cache is intentionally not
 * persisted — a hard page reload starts fresh.
 */
class CurationMediaCacheService extends BaseService {
  constructor() {
    super();

    // Map preserves insertion order, which we use for LRU eviction.
    this.cache = new Map();
  }

  has(curationId) {
    return this.cache.has(curationId);
  }

  get(curationId) {
    if (!this.cache.has(curationId)) {
      return undefined;
    }

    // Touch the entry so it becomes most-recently-used.
    const data = this.cache.get(curationId);
    this.cache.delete(curationId);
    this.cache.set(curationId, data);

    return data;
  }

  set(curationId, data) {
    // Re-insert so this entry is most-recently-used.
    this.cache.delete(curationId);
    this.cache.set(curationId, data);

    while (this.cache.size > MAX_ENTRIES) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
  }

  invalidate(curationId) {
    this.cache.delete(curationId);
  }

  clear() {
    this.cache.clear();
  }
}

export default CurationMediaCacheService;
